
import React, { useState } from 'react';
import { Insumo, UnidadeMedida } from '../../types';
import { useInsumos } from '../../hooks/useInsumos';
import { formatCurrency } from '../../constants';

interface AdminInsumosProps {
  empresaId: string;
  isDemo?: boolean;
}

export const AdminInsumos: React.FC<AdminInsumosProps> = ({ empresaId, isDemo }) => {
  const { insumos, unidades, loading, saveInsumo, deleteInsumo, saveUnidade } = useInsumos(empresaId);
  const [isEditing, setIsEditing] = useState(false);
  const [currentInsumo, setCurrentInsumo] = useState<Partial<Insumo> | null>(null);
  
  const [isAddingUnidade, setIsAddingUnidade] = useState(false);
  const [newUnidade, setNewUnidade] = useState({ nome: '', sigla: '' });

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isDemo) return alert("Modo Demo: Ação não permitida.");
    if (!currentInsumo?.unidade_medida_id) return alert("Selecione uma unidade de medida.");
    try {
      await saveInsumo(currentInsumo!);
      setIsEditing(false);
      setCurrentInsumo(null);
    } catch (err: any) {
      alert(err.message);
    }
  };

  const handleSaveUnidade = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isDemo) return alert("Modo Demo: Ação não permitida.");
    try {
      const created = await saveUnidade(newUnidade);
      setIsAddingUnidade(false);
      setNewUnidade({ nome: '', sigla: '' });
      if (currentInsumo) {
        setCurrentInsumo({ ...currentInsumo, unidade_medida_id: created.id });
      }
    } catch (err: any) {
      alert(err.message);
    }
  };

  return (
    <div className="p-4 md:p-10 animate-fade-in space-y-6 max-w-7xl mx-auto">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center bg-slate-900 p-6 md:p-8 rounded-3xl shadow-2xl gap-4">
        <div>
          <h3 className="text-xl md:text-2xl font-black text-white tracking-tight">Gestão de Insumos</h3>
          <p className="text-slate-400 font-bold text-xs md:text-sm mt-1">Cadastre ingredientes e insumos para suas fichas técnicas.</p>
        </div>
        <button 
          onClick={() => { setIsEditing(true); setCurrentInsumo({ nome: '', custo_unitario: 0, estoque_atual: 0, estoque_minimo: 0, unidade_medida_id: unidades[0]?.id || '' }); }}
          className="w-full md:w-auto bg-orange-600 text-white px-6 py-4 rounded-2xl font-black shadow-xl hover:bg-orange-700 transition-all flex items-center justify-center gap-3"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M12 4v16m8-8H4"></path></svg>
          Novo Insumo
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {insumos.map(insumo => (
          <div key={insumo.id} className="bg-white border border-slate-200 p-6 rounded-[2rem] shadow-sm hover:shadow-md transition-all">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h4 className="font-black text-slate-900 text-lg">{insumo.nome}</h4>
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                  {insumo.unidade_medida?.nome} ({insumo.unidade_medida?.sigla})
                </p>
              </div>
              <div className="flex gap-2">
                <button onClick={() => { setCurrentInsumo(insumo); setIsEditing(true); }} className="p-2 text-slate-400 hover:text-orange-600 transition-colors">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"></path></svg>
                </button>
                <button onClick={() => { if(confirm('Excluir insumo?')) deleteInsumo(insumo.id); }} className="p-2 text-slate-400 hover:text-red-600 transition-colors">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path></svg>
                </button>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4 pt-4 border-t border-slate-100">
              <div>
                <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">Custo Un.</p>
                <p className="font-bold text-slate-900">{formatCurrency(insumo.custo_unitario)}</p>
              </div>
              <div>
                <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">Estoque</p>
                <p className={`font-bold ${insumo.estoque_atual <= insumo.estoque_minimo ? 'text-red-600' : 'text-slate-900'}`}>
                  {insumo.estoque_atual} {insumo.unidade_medida?.sigla}
                </p>
              </div>
            </div>
          </div>
        ))}
        {insumos.length === 0 && !loading && (
          <div className="col-span-full py-20 text-center bg-white border-2 border-dashed border-slate-200 rounded-[3rem]">
            <p className="font-bold text-slate-400">Nenhum insumo cadastrado ainda.</p>
          </div>
        )}
      </div>

      {isEditing && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[110] flex items-center justify-center p-4">
          <div className="bg-white rounded-[2.5rem] w-full max-w-lg p-8 md:p-10 shadow-2xl animate-fade-in overflow-y-auto max-h-[90vh] custom-scrollbar">
            <div className="flex justify-between items-center mb-8">
              <h3 className="text-2xl font-black text-slate-900 tracking-tight">
                {currentInsumo?.id ? 'Editar Insumo' : 'Novo Insumo'}
              </h3>
              <button onClick={() => setIsEditing(false)} className="text-slate-400 hover:text-slate-900">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg>
              </button>
            </div>

            <form onSubmit={handleSave} className="space-y-6">
              <div>
                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 ml-1">Nome do Insumo</label>
                <input required value={currentInsumo?.nome || ''} onChange={e => setCurrentInsumo({...currentInsumo, nome: e.target.value})} className="w-full px-5 py-4 bg-slate-50 border border-slate-200 rounded-2xl focus:ring-2 focus:ring-orange-600 outline-none font-semibold transition-all" placeholder="Ex: Queijo Mussarela" />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <div className="flex justify-between items-center mb-2 ml-1">
                    <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest">Unidade</label>
                    <button type="button" onClick={() => setIsAddingUnidade(true)} className="text-[9px] font-black text-orange-600 uppercase tracking-widest hover:underline">+ Nova</button>
                  </div>
                  {unidades.length > 0 ? (
                    <select required value={currentInsumo?.unidade_medida_id || ''} onChange={e => setCurrentInsumo({...currentInsumo, unidade_medida_id: e.target.value})} className="w-full px-5 py-4 bg-slate-50 border border-slate-200 rounded-2xl focus:ring-2 focus:ring-orange-600 outline-none font-semibold transition-all cursor-pointer">
                      <option value="">Selecione...</option>
                      {unidades.map(u => <option key={u.id} value={u.id}>{u.nome} ({u.sigla})</option>)}
                    </select>
                  ) : (
                    <div className="p-4 bg-orange-50 border border-orange-100 rounded-2xl text-[11px] font-bold text-orange-600 flex flex-col items-center gap-2">
                       Nenhuma unidade cadastrada.
                       <button type="button" onClick={() => setIsAddingUnidade(true)} className="bg-orange-600 text-white px-3 py-1.5 rounded-lg text-[9px] font-black uppercase tracking-tight">+ Cadastrar agora</button>
                    </div>
                  )}
                </div>
                <div>
                  <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 ml-1">Custo Unitário</label>
                  <input required type="number" step="0.01" value={currentInsumo?.custo_unitario || 0} onChange={e => setCurrentInsumo({...currentInsumo, custo_unitario: Number(e.target.value)})} className="w-full px-5 py-4 bg-slate-50 border border-slate-200 rounded-2xl focus:ring-2 focus:ring-orange-600 outline-none font-semibold transition-all" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 ml-1">Estoque Atual</label>
                  <input required type="number" step="0.01" value={currentInsumo?.estoque_atual || 0} onChange={e => setCurrentInsumo({...currentInsumo, estoque_atual: Number(e.target.value)})} className="w-full px-5 py-4 bg-slate-50 border border-slate-200 rounded-2xl focus:ring-2 focus:ring-orange-600 outline-none font-semibold transition-all" />
                </div>
                <div>
                  <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 ml-1">Estoque Mínimo</label>
                  <input required type="number" step="0.01" value={currentInsumo?.estoque_minimo || 0} onChange={e => setCurrentInsumo({...currentInsumo, estoque_minimo: Number(e.target.value)})} className="w-full px-5 py-4 bg-slate-50 border border-slate-200 rounded-2xl focus:ring-2 focus:ring-orange-600 outline-none font-semibold transition-all" />
                </div>
              </div>

              <button type="submit" disabled={loading} className="w-full bg-slate-900 text-white font-black py-5 rounded-2xl hover:bg-orange-600 transition-all shadow-xl disabled:opacity-50">
                {loading ? 'Processando...' : 'Salvar Insumo'}
              </button>
            </form>
          </div>
        </div>
      )}

      {isAddingUnidade && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[120] flex items-center justify-center p-4">
          <div className="bg-white rounded-[2.5rem] w-full max-w-sm p-8 shadow-2xl animate-fade-in">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-black text-slate-900 tracking-tight">Nova Unidade</h3>
              <button onClick={() => setIsAddingUnidade(false)} className="text-slate-400 hover:text-slate-900">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg>
              </button>
            </div>

            <form onSubmit={handleSaveUnidade} className="space-y-6">
              <div>
                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 ml-1">Nome da Unidade</label>
                <input required value={newUnidade.nome} onChange={e => setNewUnidade({...newUnidade, nome: e.target.value})} className="w-full px-5 py-4 bg-slate-50 border border-slate-200 rounded-2xl focus:ring-2 focus:ring-orange-600 outline-none font-semibold transition-all" placeholder="Ex: Gramas" />
              </div>
              
              <div>
                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 ml-1">Sigla</label>
                <input required value={newUnidade.sigla} onChange={e => setNewUnidade({...newUnidade, sigla: e.target.value})} className="w-full px-5 py-4 bg-slate-50 border border-slate-200 rounded-2xl focus:ring-2 focus:ring-orange-600 outline-none font-semibold transition-all" placeholder="Ex: g" />
              </div>

              <button type="submit" disabled={loading} className="w-full bg-slate-900 text-white font-black py-5 rounded-2xl hover:bg-orange-600 transition-all shadow-xl disabled:opacity-50">
                {loading ? 'Salvando...' : 'Salvar Unidade'}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
